import {
  colors,
  seriesMeta,
  thermalAxisRanges,  trendMetricMeta,
} from "../core/config.js";
import {
  formatElapsedTime,
} from "../core/format.js";
import {
  clamp01,
  extentFromValues,
} from "../core/math.js";
import {
  yLabelsForRange,
} from "./scales.js";
import { lerp } from "../core/math.js";

function titleCase(value) {
  return String(value ?? "").replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const METRIC_DISPLAY_DICTIONARY = {
  egt: ["EGT avg", " F", 0],
  cht: ["CHT avg", " F", 0],
  egt1: ["EGT1", " F", 0],
  egt2: ["EGT2", " F", 0],
  egt3: ["EGT3", " F", 0],
  egt4: ["EGT4", " F", 0],
  egt5: ["EGT5", " F", 0],
  egt6: ["EGT6", " F", 0],
  cht1: ["CHT1", " F", 0],
  cht2: ["CHT2", " F", 0],
  cht3: ["CHT3", " F", 0],
  cht4: ["CHT4", " F", 0],
  cht5: ["CHT5", " F", 0],
  cht6: ["CHT6", " F", 0],
  cht7: ["CHT7", " F", 0],
  cht8: ["CHT8", " F", 0],
  cht9: ["CHT9", " F", 0],
  cht10: ["CHT10", " F", 0],
  cht11: ["CHT11", " F", 0],
  cht12: ["CHT12", " F", 0],
  cht13: ["CHT13", " F", 0],
  cht14: ["CHT14", " F", 0],
  cht15: ["CHT15", " F", 0],
  cht16: ["CHT16", " F", 0],
  egt7: ["EGT7", " F", 0],
  egt8: ["EGT8", " F", 0],
  egt9: ["EGT9", " F", 0],
  egt10: ["EGT10", " F", 0],
  egt11: ["EGT11", " F", 0],
  egt12: ["EGT12", " F", 0],
  egt13: ["EGT13", " F", 0],
  egt14: ["EGT14", " F", 0],
  egt15: ["EGT15", " F", 0],
  egt16: ["EGT16", " F", 0],
  fuelFlow: ["FF", " GPH", 1],
  fuelFlow1: ["FF", " GPH", 1],
  fuelFlow2: ["FF", " GPH", 1],
  fuelFlowLeft: ["FF", " GPH", 1],
  fuelFlowRight: ["FF", " GPH", 1],
  fuelUsed: ["Fuel Used", " gal", 1],
  fuelUsed1: ["Fuel Used", " gal", 1],
  fuelUsed2: ["Fuel Used", " gal", 1],
  fuelUsedLeft: ["Fuel Used", " gal", 1],
  fuelUsedRight: ["Fuel Used", " gal", 1],
  fuelPressure: ["Fuel P", " psi", 1],
  tit: ["TIT", " F", 0],
  tit1: ["TIT", " F", 0],
  tit2: ["TIT", " F", 0],
  upperDeckPressure: ["Upper Deck", " psi", 1],
  inductionTemp: ["Induction", " F", 0],
  compressorDischargeTemp: ["CDT", " F", 0],
  buss: ["Bus", " V", 1],
  amps: ["Amps", " A", 1],
  amps1: ["Amps", " A", 1],
  amps2: ["Amps", " A", 1],
  ampsLeft: ["Amps", " A", 1],
  ampsRight: ["Amps", " A", 1],
  oat: ["OAT", " F", 0],
  carbTemp: ["Carb", " F", 0],
  oilTemp: ["Oil Temp", " F", 0],
  oilTemp1: ["Oil Temp", " F", 0],
  oilTemp2: ["Oil Temp", " F", 0],
  oilTempLeft: ["Oil Temp", " F", 0],
  oilTempRight: ["Oil Temp", " F", 0],
  oilPressure: ["Oil Press", " psi", 0],
  oilPressure1: ["Oil Press", " psi", 0],
  oilPressure2: ["Oil Press", " psi", 0],
  oilPressureLeft: ["Oil Press", " psi", 0],
  oilPressureRight: ["Oil Press", " psi", 0],
  rpm: ["RPM", "", 0],
  rpm1: ["RPM", "", 0],
  rpm2: ["RPM", "", 0],
  rpmLeft: ["RPM", "", 0],
  rpmRight: ["RPM", "", 0],
  map: ["MAP", " inHg", 1],
  map1: ["MAP", " inHg", 1],
  map2: ["MAP", " inHg", 1],
  mapLeft: ["MAP", " inHg", 1],
  mapRight: ["MAP", " inHg", 1],
  percentPower: ["Engine Power", "%", 0],
  powerRopHp: ["Power ROP", " hp", 0],
  percentPowerRop: ["Power ROP", "%", 0],
  powerLopHp: ["Power LOP", " hp", 0],
  percentPowerLop: ["Power LOP", "%", 0],
  ias: ["IAS", " kt", 0],
  tas: ["TAS", " kt", 0],
  groundSpeed: ["GS", " kt", 0],
  altitude: ["Alt", " ft", 0],
  verticalSpeed: ["VS", " fpm", 0],
};

function descriptorChannelLabelBase(descriptor) {
  if (!descriptor) return "";
  return String(descriptor.label ?? descriptor.shortLabel ?? "").replace(/\s+Engine$/i, "").trim();
}

function buildKeyToEngineMap(descriptors = []) {
  const map = new Map();
  if (!Array.isArray(descriptors) || descriptors.length < 2) return map;
  for (const descriptor of descriptors) {
    const channels = descriptor?.channels ?? {};
    for (const channelKey of Object.values(channels)) {
      if (typeof channelKey === "string" && channelKey) map.set(channelKey, descriptor);
    }
  }
  return map;
}

export function createMetricDisplayFor(descriptors = []) {
  const keyToEngine = buildKeyToEngineMap(descriptors);
  return function metricDisplayFromContext(key, point) {
    const display = METRIC_DISPLAY_DICTIONARY[key];
    const value = point?.[key];
    if (!display || !Number.isFinite(value)) return null;
    const prefix = descriptorChannelLabelBase(keyToEngine.get(key));
    return {
      label: prefix ? `${prefix} ${display[0]}` : display[0],
      suffix: display[1],
      digits: display[2],
      value,
    };
  };
}

export const metricDisplayFor = createMetricDisplayFor();

export function isTitMetricKey(key) {
  return /^tit\d*$/i.test(String(key ?? ""));
}

export function isTurboMetricKey(key) {
  const normalized = String(key ?? "").toLowerCase();
  return isTitMetricKey(normalized)
    || normalized.includes("upperdeck")
    || normalized.includes("induction")
    || normalized.includes("compressor")
    || normalized.includes("fuelpressure");
}

export function createMarkerMetricTextFor(descriptors = [], fallbackSeriesMeta = {}) {
  const displayFn = createMetricDisplayFor(descriptors);
  return function markerMetricTextFromContext(key, point, seriesMeta = fallbackSeriesMeta) {
    const metric = displayFn(key, point);
    if (!metric) {
      const label = seriesMeta[key]?.label ?? titleCase(key);
      return `${label} n/a`;
    }
    return `${metric.label} ${Number(metric.value).toFixed(metric.digits)}${metric.suffix}`;
  };
}

export const markerMetricTextFor = createMarkerMetricTextFor();


export function detectionHighlightStyle(key) {
  return {
    sensors: { fill: "rgba(47, 136, 255, 0.12)", stroke: "rgba(47, 136, 255, 0.82)", text: colors.blue },
    cht: { fill: "rgba(255, 159, 28, 0.12)", stroke: "rgba(255, 159, 28, 0.82)", text: colors.amber },
    egt: { fill: "rgba(179, 107, 255, 0.12)", stroke: "rgba(179, 107, 255, 0.82)", text: colors.purple },
    valve: { fill: "rgba(98, 206, 77, 0.11)", stroke: "rgba(98, 206, 77, 0.82)", text: colors.green },
    combustion: { fill: "rgba(255, 73, 61, 0.11)", stroke: "rgba(255, 73, 61, 0.82)", text: colors.red },
    divergence: { fill: "rgba(45, 212, 191, 0.11)", stroke: "rgba(45, 212, 191, 0.82)", text: colors.teal },
    descentEgtDropout: { fill: "rgba(179, 107, 255, 0.12)", stroke: "rgba(179, 107, 255, 0.82)", text: colors.purple },
    inductionLeak: { fill: "rgba(255, 159, 28, 0.12)", stroke: "rgba(255, 159, 28, 0.85)", text: colors.amber },
    mag: { fill: "rgba(255, 159, 28, 0.12)", stroke: "rgba(255, 159, 28, 0.85)", text: colors.amber },
    oilSystem: { fill: "rgba(98, 206, 77, 0.12)", stroke: "rgba(98, 206, 77, 0.84)", text: colors.green },
    coolantMargin: { fill: "rgba(45, 212, 191, 0.12)", stroke: "rgba(45, 212, 191, 0.86)", text: colors.teal },
    carbIce: { fill: "rgba(247, 207, 99, 0.12)", stroke: "rgba(247, 207, 99, 0.86)", text: "#f7cf63" },
    cooling: { fill: "rgba(45, 212, 191, 0.11)", stroke: "rgba(45, 212, 191, 0.82)", text: colors.teal },
    coolingRate: { fill: "rgba(255, 159, 28, 0.12)", stroke: "rgba(255, 159, 28, 0.85)", text: colors.amber },
    charging: { fill: "rgba(34, 197, 94, 0.11)", stroke: "rgba(34, 197, 94, 0.82)", text: colors.green },
    electrical: { fill: "rgba(47, 136, 255, 0.12)", stroke: "rgba(47, 136, 255, 0.82)", text: colors.blue },
    gami: { fill: "rgba(45, 212, 191, 0.13)", stroke: "rgba(45, 212, 191, 0.9)", text: colors.teal },
    "pilot-observation": {
      fill: "rgba(245, 183, 65, 0.16)",
      stroke: "rgba(245, 183, 65, 0.9)",
      text: "#f5b741",
    },
  }[key] ?? { fill: "rgba(255, 255, 255, 0.1)", stroke: "rgba(255, 255, 255, 0.75)", text: colors.text };
}

export function detectionWindowGeometry(data, bounds, window) {
  if (!window || !data.length) return null;
  const visibleTimes = data.map((point) => point.elapsedSeconds).filter(Number.isFinite);
  const extent = extentFromValues(visibleTimes);
  if (!extent || extent.max <= extent.min) return null;
  if (window.endTime < extent.min || window.startTime > extent.max) return null;

  const startRatio = clamp01((Math.max(window.startTime, extent.min) - extent.min) / (extent.max - extent.min));
  const endRatio = clamp01((Math.min(window.endTime, extent.max) - extent.min) / (extent.max - extent.min));
  const x = lerp(bounds.left, bounds.right, startRatio);
  const right = lerp(bounds.left, bounds.right, endRatio);
  return { window, x, right, width: Math.max(3, right - x) };
}

export function detectionWindowEvidenceLabel(window) {
  if (!window) return "";
  if (window.evidenceLabel) return String(window.evidenceLabel);
  if (!window.channelLabel || !Number.isFinite(window.value)) return "";
  const suffix = window.valueSuffix ?? "";
  const digits = Number.isFinite(window.valueDigits) ? window.valueDigits : 0;
  return `${window.channelLabel} ${Number(window.value).toFixed(digits)}${suffix}`;
}

export function detectionWindowLabelPlan(ctx, geometry, bounds, reservedRects = []) {
  const { window, x, right } = geometry;
  const label = detectionWindowEvidenceLabel(window) || window.label;
  ctx.font = "700 12px Inter, sans-serif";
  const maxTextWidth = Math.max(80, bounds.right - bounds.left - 24);
  let displayLabel = label;
  while (displayLabel.length > 12 && ctx.measureText(displayLabel).width > maxTextWidth) {
    displayLabel = `${displayLabel.slice(0, -4).trim()}...`;
  }
  const textWidth = Math.min(ctx.measureText(displayLabel).width, maxTextWidth);
  const labelY = Math.max(14, bounds.top - 32);
  const labelHeight = 20;
  const clampLabelX = (candidate) => Math.min(bounds.right - textWidth - 6, Math.max(bounds.left + 6, candidate));
  const candidates = [
    clampLabelX(x + 8),
    clampLabelX(right - textWidth - 8),
    clampLabelX(bounds.left + 8),
    clampLabelX(bounds.right - textWidth - 8),
  ];
  let labelX = candidates[0];
  for (const candidate of candidates) {
    const candidateRect = {
      left: candidate - 6,
      right: candidate + textWidth + 6,
      top: labelY - 13,
      bottom: labelY - 13 + labelHeight,
    };
    if (!reservedRects.some((rect) => rect.left < candidateRect.right && rect.right > candidateRect.left && rect.top < candidateRect.bottom && rect.bottom > candidateRect.top)) {
      labelX = candidate;
      break;
    }
  }
  const rect = {
    left: labelX - 6,
    right: labelX + textWidth + 6,
    top: labelY - 13,
    bottom: labelY - 13 + labelHeight,
  };
  return {
    displayLabel,
    labelX,
    labelY,
    textWidth,
    rect,
    style: detectionHighlightStyle(window.key),
  };
}

export function emptyChartMessagePlan(ctx, bounds, message) {
  const textWidth = ctx.measureText(message).width;
  return {
    message,
    x: bounds.left + (bounds.right - bounds.left - textWidth) / 2,
    y: bounds.top + (bounds.bottom - bounds.top) / 2,
    textWidth,
  };
}

export function laneLabelPlan(ctx, bounds, label, options: Record<string, any> = {}) {
  const textWidth = ctx.measureText(label).width;
  const x = options.align === "right" ? bounds.right - textWidth - 8 : bounds.left + 8;
  const y = Math.max(14, bounds.top - 16);
  return {
    label,
    x,
    y,
    textWidth,
    color: options.color ?? colors.muted,
    rect: {
      left: x - 6,
      right: x + textWidth + 6,
      top: y - 13,
      bottom: y + 7,
    },
  };
}

export function chartMarkerX(bounds, ratio) {
  return lerp(bounds.left, bounds.right, clamp01(ratio));
}

export function chartZoomSelectionPlan(bounds, dragSelection, canvasId) {
  if (!dragSelection || dragSelection.canvasId !== canvasId) return null;
  if (dragSelection.mode === "pan") return null;
  const startX = chartMarkerX(bounds, dragSelection.start);
  const endX = chartMarkerX(bounds, dragSelection.current);
  const x = Math.min(startX, endX);
  const width = Math.abs(endX - startX);
  if (width < 2) return null;
  return {
    x,
    y: bounds.top,
    width,
    height: Math.max(0, bounds.bottom - bounds.top),
  };
}

export function chartOverlayPlan(bounds, markerRatio, dragSelection, canvasId) {
  const markerX = chartMarkerX(bounds, markerRatio);
  return {
    marker: {
      x: markerX,
      top: bounds.top,
      height: Math.max(0, bounds.bottom - bounds.top),
    },
    zoom: chartZoomSelectionPlan(bounds, dragSelection, canvasId),
  };
}

export const MIN_CHART_ZOOM_WINDOW = 0.001;

// Clamp a candidate zoom window (start + width, both 0-1 flight ratios) so it
// stays inside the flight and never collapses below the minimum width.
export function clampedZoomWindow(start, width, minWidth = MIN_CHART_ZOOM_WINDOW) {
  const safeWidth = Math.max(minWidth, Math.min(1, Number.isFinite(width) ? width : 1));
  const safeStart = Math.max(0, Math.min(Number.isFinite(start) ? start : 0, 1 - safeWidth));
  return { start: safeStart, end: safeStart + safeWidth };
}

// Scale the current window by `factor` (<1 zooms in, >1 zooms out), keeping
// `centerRatio` (absolute 0-1 flight ratio) centered when provided.
export function steppedZoomWindow(zoomRange, factor, centerRatio = null, minWidth = MIN_CHART_ZOOM_WINDOW) {
  const width = Math.max(minWidth, (zoomRange?.end ?? 1) - (zoomRange?.start ?? 0));
  const safeFactor = Number.isFinite(factor) && factor > 0 ? factor : 1;
  const nextWidth = width * safeFactor;
  const center = Number.isFinite(centerRatio)
    ? clamp01(centerRatio)
    : ((zoomRange?.start ?? 0) + (zoomRange?.end ?? 1)) / 2;
  return clampedZoomWindow(center - nextWidth / 2, nextWidth, minWidth);
}

// Pinch gesture: the window captured at pinch start (`anchorZoom`) scales by
// 1/scale while the flight time under the fingers' midpoint stays pinned to
// the midpoint, so the same formula covers pinch-zoom and two-finger pan.
export function pinchZoomWindow({ anchorZoom, scale, anchorMidRatio, currentMidRatio, minWidth = MIN_CHART_ZOOM_WINDOW }) {
  const anchorWidth = Math.max(1e-6, (anchorZoom?.end ?? 1) - (anchorZoom?.start ?? 0));
  const safeScale = Math.max(0.02, Math.min(50, Number.isFinite(scale) && scale > 0 ? scale : 1));
  const nextWidth = Math.max(minWidth, Math.min(1, anchorWidth / safeScale));
  const anchorAbsolute = (anchorZoom?.start ?? 0) + clamp01(anchorMidRatio) * anchorWidth;
  return clampedZoomWindow(anchorAbsolute - clamp01(currentMidRatio) * nextWidth, nextWidth, minWidth);
}

export function chartGestureHintText({ coarse = false, selectRegion = false } = {}) {
  if (selectRegion) return "Drag across the graph to zoom to that range";
  if (coarse) return "Pinch to zoom • Two-finger drag to pan • Double tap to zoom";
  return "Drag to zoom • Right-drag to pan";
}

export function inlineLegendItems(bounds, labels, legendColors, options: Record<string, any> = {}) {
  const spacing = options.spacing ?? 150;
  const y = bounds.top - 14;
  return labels.map((label, index) => {
    const x = bounds.left + index * spacing;
    return {
      label,
      color: legendColors[index],
      line: {
        x1: x,
        y1: y - 4,
        x2: x + 24,
        y2: y - 4,
      },
      text: {
        x: x + 30,
        y,
      },
    };
  });
}

export function trendMetricValue(row, key) {
  const value = row?.[key];
  return Number.isFinite(value) ? value : null;
}

export function formatTrendValue(value, key, metaByKey = trendMetricMeta) {
  const meta = metaByKey[key] ?? metaByKey.overallScore;
  if (!Number.isFinite(value)) return "--";
  return `${Number(value).toFixed(meta.digits)}${meta.suffix}`;
}

export function trendScale(rows, key, metaByKey = trendMetricMeta) {
  const meta = metaByKey[key] ?? metaByKey.overallScore;
  if (Number.isFinite(meta.min) && Number.isFinite(meta.max)) {
    return { min: meta.min, max: meta.max };
  }
  const values = rows.map((row) => trendMetricValue(row, key)).filter(Number.isFinite);
  const extent = extentFromValues(values);
  if (!extent) return { min: 0, max: 1 };
  const span = Math.max(1, extent.max - extent.min);
  return {
    min: Math.max(0, extent.min - span * 0.12),
    max: extent.max + span * 0.12,
  };
}

export function trendAxisLabels(scale, key, metaByKey = trendMetricMeta) {
  const meta = metaByKey[key] ?? metaByKey.overallScore;
  return Array.from({ length: 5 }, (_, index) => {
    const value = meta.invertYAxis
      ? lerp(scale.min, scale.max, index / 4)
      : lerp(scale.max, scale.min, index / 4);
    return formatTrendValue(value, key, metaByKey);
  });
}

export function scalePoint(value, min, max, low, high) {
  if (max === min) return (low + high) / 2;
  return lerp(high, low, (value - min) / (max - min));
}

export function trendChartPoints(rows, bounds, scale, key) {
  const meta = trendMetricMeta[key] ?? trendMetricMeta.overallScore;
  const validRows = rows.filter((row) => Number.isFinite(trendMetricValue(row, key)));
  return validRows.map((row, index) => {
    const value = trendMetricValue(row, key);
    const x = lerp(bounds.left, bounds.right, index / Math.max(validRows.length - 1, 1));
    const y = meta.invertYAxis
      ? lerp(bounds.top, bounds.bottom, (value - scale.min) / (scale.max - scale.min || 1))
      : scalePoint(value, scale.min, scale.max, bounds.top, bounds.bottom);
    return { row, index, value, x, y };
  });
}

export function trendDateLabelItems(ctx, points, bounds, maxLabels = 5) {
  const labelCount = Math.min(maxLabels, points.length);
  if (!labelCount) return [];
  return Array.from({ length: labelCount }, (_, index) => {
    const pointIndex = Math.round(index * (points.length - 1) / Math.max(labelCount - 1, 1));
    const point = points[pointIndex];
    const text = point.row.dateLabel;
    const textWidth = ctx.measureText(text).width;
    return {
      text,
      x: Math.min(bounds.right - textWidth, Math.max(bounds.left, point.x - 30)),
      y: bounds.bottom + 28,
      pointIndex,
      textWidth,
    };
  });
}

export function trendLatestMarker(points, key, metaByKey = trendMetricMeta) {
  const latest = points.at(-1);
  if (!latest) return null;
  const meta = metaByKey[key] ?? metaByKey.overallScore;
  return {
    ratio: points.length > 1 ? 1 : 0,
    label: latest.row.dateLabel,
    lines: [
      `${meta.label}: ${formatTrendValue(latest.value, key, metaByKey)}`,
      `Flight: ${latest.row.title}`,
    ],
  };
}

export function trendCylinderScale(rows) {
  const cylinderRows = rows.filter((row) => row.chtByCylinder?.some((item) => Number.isFinite(item.peak)));
  const values = cylinderRows.flatMap((row) => row.chtByCylinder.map((item) => item.peak)).filter(Number.isFinite);
  const extent = extentFromValues(values);
  if (!extent) return null;
  const span = Math.max(30, extent.max - extent.min);
  const min = Math.max(0, extent.min - span * 0.15);
  const max = extent.max + span * 0.15;
  return { rows: cylinderRows, min, max };
}

export function trendCylinderAxisLabels(scale) {
  return Array.from({ length: 5 }, (_, index) => `${Math.round(lerp(scale.max, scale.min, index / 4))}`);
}

export function trendCylinderSeries(scale, bounds, cylinderCount = 6) {
  if (!scale) return [];
  const denominator = Math.max(scale.rows.length - 1, 1);
  return Array.from({ length: cylinderCount }, (_, index) => {
    const cylinder = index + 1;
    const points = scale.rows.map((row, rowIndex) => {
      const value = row.chtByCylinder.find((item) => item.cylinder === cylinder)?.peak;
      if (!Number.isFinite(value)) return null;
      return {
        row,
        index: rowIndex,
        value,
        x: lerp(bounds.left, bounds.right, rowIndex / denominator),
        y: scalePoint(value, scale.min, scale.max, bounds.top, bounds.bottom),
      };
    }).filter(Boolean);
    return { cylinder, points };
  }).filter((series) => series.points.length >= 2);
}

export function compareChartPlan(data, referenceData, markerPoint = null, markerLabel = "") {
  const hasRpm = data.some((point) => Number.isFinite(point.rpm));
  if (hasRpm) {
    return {
      mode: "power",
      grid: { labels: ["3000", "2500", "2000", "1500", "1000"], options: {} },
      lines: [
        { data, key: "rpm", meta: { color: colors.blue, min: 1000, max: 3000 }, options: {} },
        { data: referenceData, key: "rpm", meta: { color: colors.blue, min: 1000, max: 3000 }, options: { dash: [6, 5] } },
        { data, key: "map", meta: { color: colors.green, min: 10, max: 30 }, options: { min: 10, max: 30 } },
        { data: referenceData, key: "map", meta: { color: colors.green, min: 10, max: 30 }, options: { min: 10, max: 30, dash: [6, 5] } },
      ],
      legend: {
        labels: ["RPM - Current", "RPM - Reference", "MAP - Current", "MAP - Reference"],
        colors: [colors.blue, colors.blue, colors.green, colors.green],
      },
      marker: {
        ratio: 0.45,
        label: "2:32 PM",
        lines: ["RPM current: 2450", "RPM ref: 2410", "MAP current: 22.1", "MAP ref: 21.3"],
      },
    };
  }

  return {
    mode: "thermal",
    grid: {
      labels: yLabelsForRange(thermalAxisRanges.egt),
      options: {
        leftLabelColor: thermalAxisRanges.egt.color,
        rightLabels: yLabelsForRange(thermalAxisRanges.cht),
        rightLabelColor: thermalAxisRanges.cht.color,
      },
    },
    lines: [
      { data, key: "egt", meta: seriesMeta.egt, options: { min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max } },
      { data: referenceData, key: "egt", meta: seriesMeta.egt, options: { min: thermalAxisRanges.egt.min, max: thermalAxisRanges.egt.max, dash: [6, 5] } },
      { data, key: "cht", meta: seriesMeta.cht, options: { min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max } },
      { data: referenceData, key: "cht", meta: seriesMeta.cht, options: { min: thermalAxisRanges.cht.min, max: thermalAxisRanges.cht.max, dash: [6, 5] } },
    ],
    legend: {
      labels: ["EGT - Insight", "EGT - Reference", "CHT - Insight", "CHT - Reference"],
      colors: [colors.purple, colors.purple, colors.amber, colors.amber],
    },
    marker: markerPoint ? {
      ratio: 0.45,
      label: markerLabel,
      lines: [
        `EGT avg: ${Math.round(markerPoint.egt)} F`,
        `CHT avg: ${Math.round(markerPoint.cht)} F`,
        `FF: ${(markerPoint.fuelFlow ?? 0).toFixed(1)} GPH`,
        `Bus: ${(markerPoint.buss ?? 0).toFixed(1)} V`,
      ],
    } : null,
  };
}

export function cylinderCompareChartPlan(data, referenceData, cylinderCount = 6) {
  const series = [];
  for (let index = 0; index < cylinderCount; index += 1) {
    const offset = index * 22;
    const current = data.map((point) => ({ ...point, egt: point.egt - 120 + offset, cht: point.cht - 50 + index * 13 }));
    const previous = referenceData.map((point) => ({ ...point, egt: point.egt - 120 + offset, cht: point.cht - 50 + index * 10 }));
    series.push(
      { data: current, key: "egt", meta: { color: colors.purple, min: 600, max: 1800 }, options: { width: 1.4 } },
      { data: previous, key: "egt", meta: { color: colors.purple, min: 600, max: 1800 }, options: { width: 1.2, dash: [5, 5] } },
      { data: current, key: "cht", meta: { color: colors.amber, min: 100, max: 500 }, options: { min: 100, max: 500, width: 1.4 } },
      { data: previous, key: "cht", meta: { color: colors.amber, min: 100, max: 500 }, options: { min: 100, max: 500, width: 1.2, dash: [5, 5] } },
    );
  }
  return {
    grid: { labels: ["1800", "1500", "1200", "900", "600"], options: {} },
    series,
    legend: {
      labels: ["EGT 1-6 May 18", "EGT 1-6 May 4", "CHT 1-6 May 18", "CHT 1-6 May 4"],
      colors: [colors.purple, colors.purple, colors.amber, colors.amber],
    },
  };
}

export function chartLaneRenderPlan({
  data = [],
  layout,
  split = layout?.split ?? { primary: [], aux: [] },
  seriesMetaByKey = seriesMeta,
  primaryAxis = null,
  rightAxis = null,
  primaryAxisByKey = {},
  primaryGuestScale = null,
  auxScale = null,
  lineWidth = 2.3,
}: Record<string, any> = {}) {
  const hasSeries = Boolean(split.primary?.length || split.aux?.length);
  if (!data.length || !hasSeries) return { empty: true, lanes: [] };

  const lanes = [];
  if (layout?.primary && split.primary?.length && (primaryAxis || primaryGuestScale)) {
    // Channels without a pinned axis range are "guests" dragged in from the
    // secondary graph; they render aux-style against primaryGuestScale.
    const guestLabel = primaryGuestScale && split.primary.some((key) => !primaryAxisByKey[key])
      ? { label: primaryGuestScale.label, options: primaryAxis ? { align: "right" } : {} }
      : null;
    lanes.push({
      kind: "primary",
      bounds: layout.primary,
      grid: {
        labels: primaryAxis ? yLabelsForRange(primaryAxis) : primaryGuestScale.labels,
        options: {
          leftLabelColor: primaryAxis?.color,
          rightLabels: rightAxis ? yLabelsForRange(rightAxis) : [],
          rightLabelColor: rightAxis?.color,
        },
      },
      drawBackgrounds: true,
      lines: split.primary.map((key) => {
        const axis = primaryAxisByKey[key];
        if (!axis && primaryGuestScale) {
          return {
            key,
            meta: seriesMetaByKey[key],
            options: {
              width: lineWidth,
              independentScale: primaryGuestScale.independent,
              min: primaryGuestScale.range?.min,
              max: primaryGuestScale.range?.max,
            },
          };
        }
        const range = axis ?? primaryAxis;
        return {
          key,
          meta: seriesMetaByKey[key],
          options: { width: lineWidth, min: range.min, max: range.max },
        };
      }),
      labels: [
        primaryAxis ? { label: primaryAxis.label, options: { color: primaryAxis.color } } : null,
        rightAxis ? { label: rightAxis.label, options: { align: "right", color: rightAxis.color } } : null,
        rightAxis ? null : guestLabel,
      ].filter(Boolean),
    });
  }

  if (layout?.aux && split.aux?.length && auxScale) {
    lanes.push({
      kind: "aux",
      bounds: layout.aux,
      grid: {
        labels: auxScale.labels,
        options: { clear: lanes.length === 0 },
      },
      drawBackgrounds: lanes.length === 0,
      lines: split.aux.map((key) => ({
        key,
        meta: seriesMetaByKey[key],
        options: {
          width: lineWidth,
          independentScale: auxScale.independent,
          min: auxScale.range?.min,
          max: auxScale.range?.max,
        },
      })),
      labels: [{ label: auxScale.label, options: {} }],
    });
  }

  return { empty: lanes.length === 0, lanes };
}

export function explorerTimelineRatioForElapsed(data, elapsedSeconds) {
  if (!data.length || !Number.isFinite(elapsedSeconds)) return null;
  let bestIndex = -1;
  let bestDelta = Infinity;
  data.forEach((point, index) => {
    if (!Number.isFinite(point.elapsedSeconds)) return;
    const delta = Math.abs(point.elapsedSeconds - elapsedSeconds);
    if (delta < bestDelta) {
      bestDelta = delta;
      bestIndex = index;
    }
  });
  return bestIndex >= 0 ? bestIndex / Math.max(data.length - 1, 1) : null;
}

export function explorerTimelineWindowGeometry(data, window) {
  if (!data.length || !window || !Number.isFinite(window.startTime)) return null;
  const endTime = Number.isFinite(window.endTime) ? window.endTime : window.startTime;
  let firstIndex = -1;
  let lastIndex = -1;
  data.forEach((point, index) => {
    const elapsed = point.elapsedSeconds;
    if (!Number.isFinite(elapsed) || elapsed < window.startTime || elapsed > endTime) return;
    if (firstIndex < 0) firstIndex = index;
    lastIndex = index;
  });
  if (firstIndex < 0) return null;
  const denominator = Math.max(data.length - 1, 1);
  const leftRatio = firstIndex / denominator;
  const rightRatio = Math.max(firstIndex, lastIndex) / denominator;
  return {
    leftRatio,
    rightRatio,
    widthRatio: Math.max(0.004, rightRatio - leftRatio),
    startIndex: firstIndex,
    endIndex: Math.max(firstIndex, lastIndex),
  };
}

export function explorerTimelineTicks(data) {
  if (!data.length) return { minor: [], major: [] };
  const denominator = Math.max(data.length - 1, 1);
  const majorCount = Math.min(9, Math.max(2, Math.ceil(data.length / 140)));
  const majorIndexes = [];
  for (let index = 0; index < majorCount; index += 1) {
    const pointIndex = Math.round(index * denominator / Math.max(majorCount - 1, 1));
    if (!majorIndexes.includes(pointIndex)) majorIndexes.push(pointIndex);
  }
  if (!majorIndexes.includes(denominator)) majorIndexes.push(denominator);
  const minorCount = Math.min(40, Math.max(8, majorIndexes.length * 4));
  const minor = [];
  for (let index = 0; index <= minorCount; index += 1) {
    const pointIndex = Math.round(index * denominator / minorCount);
    if (majorIndexes.includes(pointIndex)) continue;
    minor.push({ left: pointIndex / denominator * 100 });
  }
  const major = majorIndexes
    .sort((a, b) => a - b)
    .map((pointIndex, index, items) => ({
      left: pointIndex / denominator * 100,
      label: formatElapsedTime(data[pointIndex]?.elapsedSeconds ?? 0),
      edge: index === 0 ? " is-first" : index === items.length - 1 ? " is-last" : "",
    }));
  return { minor, major };
}


export function explorerAlertTone(key, score, fallback = "warning") {
  if (Number.isFinite(score) && score < 58) return "danger";
  if (Number.isFinite(score) && score < 88) return "warning";
  return fallback;
}

export function explorerIsNormalFinding(key, metric) {
  const note = String(metric?.note ?? "").toLowerCase();
  if (key === "charging") return metric?.details?.status === "pass" || /\b(pass|recovered)\b/.test(note);
  if (key === "mag") return /\b(clear|consistent|identified|possible mag check)\b/.test(note) || (Number.isFinite(metric?.score) && metric.score >= 88);
  return false;
}

export function explorerConditionMarkerKey(marker) {
  return [
    marker.key ?? "",
    marker.channelKey ?? "",
    marker.shortLabel ?? marker.label ?? "",
    marker.tone ?? "",
  ].join("|");
}

export function mergeExplorerConditionMarkers(markers) {
  const byKey = new Map();
  markers.forEach((marker) => {
    const key = explorerConditionMarkerKey(marker);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(marker);
  });
  return [...byKey.values()].flatMap((group) => {
    const sorted = group.slice().sort((a, b) => a.ratioStart - b.ratioStart);
    const merged = [];
    sorted.forEach((marker) => {
      const previous = merged.at(-1);
      if (!previous || marker.ratioStart > previous.ratioEnd + 0.004) {
        merged.push({ ...marker, labels: new Set([marker.label]) });
        return;
      }
      previous.ratioStart = Math.min(previous.ratioStart, marker.ratioStart);
      previous.ratioEnd = Math.max(previous.ratioEnd, marker.ratioEnd);
      previous.labels.add(marker.label);
    });
    return merged.map((marker) => {
      const labels = [...(marker.labels ?? [])].filter(Boolean);
      const shortLabel = marker.shortLabel ?? marker.label;
      return {
        ...marker,
        label: labels.length > 1 ? `${shortLabel}: ${labels.join("; ")}` : (labels[0] ?? marker.label),
        shortLabel,
        labels: undefined,
      };
    });
  });
}

export function explorerConditionTimelineLayout(markers) {
  const laneEnds = [];
  const laidOut = mergeExplorerConditionMarkers(markers)
    .sort((a, b) => a.ratioStart - b.ratioStart || b.ratioEnd - a.ratioEnd)
    .map((marker) => {
      const lane = laneEnds.findIndex((laneEnd) => marker.ratioStart > laneEnd + 0.004);
      const conditionLane = lane >= 0 ? lane : laneEnds.length;
      laneEnds[conditionLane] = marker.ratioEnd;
      return { ...marker, conditionLane };
    })
    .sort((a, b) => (a.conditionLane - b.conditionLane) || a.ratioStart - b.ratioStart);
  return {
    markers: laidOut,
    laneCount: Math.max(laidOut.length ? 1 : 0, laneEnds.length),
  };
}
